/**
 * @fileoverview AR-805/AR-670 — project ↔ Classroom course links table (PRD
 * G1/§4A), backed by `GET /api/arc_mosaico/google_classroom_courses`. The
 * backend scopes the result: managers/managing tutors see the whole school,
 * a regular tutor sees only links backed by their own connection. Local data
 * only: it renders even while a connection needs reconnection, and never
 * calls Google — so it loads regardless of the caller's own connection.
 *
 * Layout follows the prototype's CourseLinker (Paper + title/description +
 * table with the branches: loading / empty / list). The sync toggle stays on
 * each project's card — this table is read-only.
 */

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Anchor, Badge, Box, Button, Group, Paper, Stack, Table, Text } from '@mantine/core'
import { IconAlertTriangle, IconExternalLink, IconRefresh } from '@tabler/icons-react'

import { ClassroomPortError, LinkedCourseRow, getClassroomAdapters, useRefreshOnFocus } from 'integrations/classroom'
import { CoursesTableSkeleton } from './skeletons'

/** The backing connection trumps syncEnabled: a link whose tutor disconnected
 *  (or must reconnect) is not syncing no matter what the toggle says. */
function statusBadge(course: LinkedCourseRow): { color: string; labelKey: string } {
  if (course.connectionStatus === 'disconnected') {
    return { color: 'red', labelKey: 'gcwCourseStatusDisconnected' }
  }
  if (course.connectionStatus === 'needs_reconnection') {
    return { color: 'orange', labelKey: 'gcwCourseStatusNeedsReconnection' }
  }
  return course.syncEnabled
    ? { color: 'green', labelKey: 'gcwCourseStatusActive' }
    : { color: 'gray', labelKey: 'gcwCourseStatusInactive' }
}

interface CourseLinkerProps {
  /** Caller's own connection state — only picks the empty-state copy: links
   *  made by colleagues must show even when the caller never connected. */
  readonly connected: boolean
}

export function CourseLinker({ connected }: CourseLinkerProps) {
  const { t } = useTranslation()
  const [courses, setCourses] = useState<LinkedCourseRow[]>([])
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  // Tutors and managers are allowed; a leftover 401 (student profile) hides
  // the whole card instead of showing an error they cannot act on.
  const [forbidden, setForbidden] = useState(false)
  const tr = (key: string) => t(`classroom.${key}`)

  const load = useCallback(() => {
    let cancelled = false
    setLoading(true)
    setFailed(false)
    getClassroomAdapters()
      .courses.listLinkedCourses()
      .then(result => {
        if (!cancelled) setCourses(result)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (error instanceof ClassroomPortError && error.code === 'unauthorized') setForbidden(true)
        else setFailed(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // `connected` is in the deps on purpose: disconnecting in the card right
  // above orphans this user's links in place — the rows must refetch to show
  // the Relink state instead of a stale green Active.
  useEffect(() => load(), [load, connected])

  // Links are created from the project pages (and courses in Google itself):
  // coming back to this tab must show what changed elsewhere.
  useRefreshOnFocus(load, !forbidden)

  if (forbidden) return null

  return (
    <Paper withBorder p='lg' radius='md'>
      <Stack spacing='md'>
        <div>
          <Text weight={600}>{tr('gcwLinkedCoursesTitle')}</Text>
          <Text size='sm' color='dimmed'>
            {tr('gcwLinkedCoursesDescription')}
          </Text>
        </div>

        {loading && <CoursesTableSkeleton />}

        {!loading && failed && (
          <Alert
            color='red'
            variant='light'
            icon={<IconAlertTriangle size={16} />}
            title={tr('classroomLoadCoursesFailed')}
          >
            <Stack spacing='xs' align='flex-start'>
              <Text size='sm'>{tr('gcwLinkedCoursesRetry')}</Text>
              <Button size='xs' variant='default' leftIcon={<IconRefresh size={14} />} onClick={load}>
                {tr('projectClassroomRefresh')}
              </Button>
            </Stack>
          </Alert>
        )}

        {!loading && !failed && courses.length === 0 && (
          <Text size='sm' color='dimmed'>
            {tr(connected ? 'gcwNoLinkedCourses' : 'gcwConnectFirst')}
          </Text>
        )}

        {/* One notice for ALL orphan rows: per-row badges say "Relink", this
            says why — repeating the reason on every row would be noise. */}
        {!loading && !failed && courses.some(course => course.connectionStatus === 'disconnected') && (
          <Alert color='yellow' variant='light' icon={<IconAlertTriangle size={16} />}>
            <Text size='sm'>{tr('gcwOrphanLinksNotice')}</Text>
          </Alert>
        )}

        {!loading && !failed && courses.length > 0 && (
          // Narrow screens: the columns are wider than the card, so the table
          // scrolls inside its own box instead of pushing the page.
          <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
            <Table verticalSpacing='sm' highlightOnHover>
              <thead>
                <tr>
                  <th>{tr('gcwProjectColumn')}</th>
                  <th>{tr('gcwClassColumn')}</th>
                  <th>{tr('gcwCourseColumn')}</th>
                  <th>{tr('gcwAccountColumn')}</th>
                  <th>{tr('gcwStatusColumn')}</th>
                </tr>
              </thead>
              <tbody>
                {courses.map(course => (
                  <tr key={course.id}>
                    <td>
                      <Text size='sm' weight={500}>
                        {course.project.name}
                      </Text>
                    </td>
                    <td>
                      <Text size='sm'>{course.groupName || '—'}</Text>
                    </td>
                    <td>
                      <Group spacing={6} noWrap>
                        <Text size='sm' truncate>
                          {course.name}
                        </Text>
                        {course.alternateLink && (
                          <Anchor
                            href={course.alternateLink}
                            target='_blank'
                            rel='noopener noreferrer'
                            aria-label={tr('gcwViewInClassroom')}
                          >
                            <IconExternalLink size={14} />
                          </Anchor>
                        )}
                      </Group>
                    </td>
                    <td>
                      <Text size='sm'>{course.connectionGoogleEmail || '—'}</Text>
                    </td>
                    <td>
                      <Badge color={statusBadge(course).color} variant='light' size='sm'>
                        {tr(statusBadge(course).labelKey)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}
      </Stack>
    </Paper>
  )
}
